import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { adminService } from "../services";
import { AddMembers } from "../schema/add-members.schema";
import { BaseGetRequestParams } from "@quest/types";
import { DenialsPayload } from "../types";

export const useAddTenantMember = () => {
  return useMutation({
    mutationKey: ["addTenantMember"],
    mutationFn: ({ data }: { data: AddMembers }) =>
      adminService.addTenantMember(data),
  });
};

export const useCreateMentor = () => {
  return useMutation({
    mutationKey: ["createMentor"],
    mutationFn: ({ data, isSchool }: { data: AddMembers; isSchool: boolean }) =>
      adminService.createMentor(data, isSchool),
  });
};

export const useCreateParents = () => {
  return useMutation({
    mutationKey: ["createParents"],
    mutationFn: ({ data }: { data: AddMembers }) =>
      adminService.createParents(data),
  });
};

export const useDeactivateMember = (
  userId: string,
  tenantSlug: string,
  isSchool: boolean,
) => {
  return useMutation({
    mutationKey: ["deactivateMember"],
    mutationFn: () =>
      adminService.deactivateMember(userId, tenantSlug, isSchool),
  });
};

export const useGetMentorRemovalImpact = (
  userId: string,
  tenantSlug: string,
  isSchool: boolean,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ["mentorRemovalImpact", userId, tenantSlug],
    queryFn: () =>
      adminService.getMentorRemovalImpact(userId, tenantSlug, isSchool),
    enabled: enabled && !!userId,
  });
};

export const useRemoveMentor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["removeMentor"],
    mutationFn: ({
      userId,
      tenantSlug,
      isSchool,
      transferToUserId,
    }: {
      userId: string;
      tenantSlug: string;
      isSchool: boolean;
      transferToUserId?: number | null;
    }) =>
      adminService.removeMentor(userId, tenantSlug, isSchool, transferToUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getMentors"] });
      queryClient.invalidateQueries({ queryKey: ["getTenantMember"] });
      queryClient.invalidateQueries({ queryKey: ["getIndividualMember"] });
      queryClient.invalidateQueries({ queryKey: ["mentorRemovalImpact"] });
    },
  });
};

export const useBulkImportMentors = () => {
  return useMutation({
    mutationKey: ["bulkImportMentors"],
    mutationFn: ({ data }: { data: FormData }) =>
      adminService.bulkImportMentors(data),
  });
};

export const useReactivateMember = (
  userId: string,
  tenantSlug: string,
  isSchool: boolean,
) => {
  // Do not invalidate mentor/member list queries here — that refetches while
  // the success modal is open, flips active=true, unmounts ReactivateMember,
  // and dismisses the modal. Invalidation happens on modal close instead.
  return useMutation({
    mutationKey: ["reactivateMember", userId, tenantSlug, isSchool],
    mutationFn: () =>
      adminService.reactivateMember(userId, tenantSlug, isSchool),
  });
};

export const useReactivateStudent = (studentId: string) => {
  return useMutation({
    mutationKey: ["reactivateStudent"],
    mutationFn: () => adminService.reactivateStudent(studentId),
  });
};

export const useDeactivateParent = (parentId: string) => {
  return useMutation({
    mutationKey: ["deactivateParent"],
    mutationFn: () => adminService.deactivateParent(parentId),
  });
};

export const useReactivateParent = (parentId: string) => {
  return useMutation({
    mutationKey: ["activateParent"],
    mutationFn: () => adminService.reactivateParent(parentId),
  });
};

export const useGetTenantMembers = (
  tenantSlug: string,
  isSchool: boolean,
  params: BaseGetRequestParams,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ["getTenantMember", tenantSlug, params],
    queryFn: () => adminService.getTenantMembers(tenantSlug, isSchool, params),
    enabled: enabled,
  });
};

export const useGetEnrolledMentors = (
  params: BaseGetRequestParams,
  enabled: boolean,
  tenantSlug = "",
  isSchool = true,
) => {
  return useQuery({
    queryKey: ["getMentors", params, tenantSlug, isSchool],
    queryFn: () =>
      adminService.getEnrolledMentorsForTenant(params, tenantSlug, isSchool),
    enabled: enabled,
  });
};

export const useGetUnenrolledMentors = (
  params: BaseGetRequestParams,
  enabled: boolean,
  tenantSlug = "",
  isSchool = true,
) => {
  return useQuery({
    queryKey: ["getUnenrolledMentors", params, tenantSlug, isSchool],
    queryFn: () =>
      adminService.getUnenrolledMentorsForTenant(params, tenantSlug, isSchool),
    enabled: enabled,
  });
};

export const useGetIndividualMember = (
  userId: string,
  tenantSlug: string,
  isSchool: boolean,
) => {
  return useQuery({
    queryKey: ["getIndividualMember", userId],
    queryFn: () =>
      adminService.getIndividualMember(userId, tenantSlug, isSchool),
    enabled: !!userId,
  });
};

export const useUpdateMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["updateMember"],
    mutationFn: ({
      userId,
      tenantSlug,
      data,
      isSchool,
    }: {
      userId: string;
      tenantSlug: string;
      data: AddMembers;
      isSchool: boolean;
    }) => adminService.updateMember(userId, tenantSlug, data, isSchool),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getTenantMember"] });
    },
  });
};

export const useResendEmail = (isSuperadmin: boolean) => {
  return useMutation({
    mutationKey: ["resendEmail"],
    mutationFn: ({
      userUUID,
      tenantSlug,
      redirectUri,
    }: {
      userUUID: string;
      tenantSlug: string;
      redirectUri: string;
    }) =>
      adminService.resendEmail(userUUID, tenantSlug, redirectUri, isSuperadmin),
  });
};

export const useGetStudents = (
  params: BaseGetRequestParams,
  enabled: boolean,
) => {
  return useInfiniteQuery({
    queryKey: ["getStudents", params],
    queryFn: ({ pageParam }) =>
      adminService.getStudents({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage?.pagination?.hasNext
        ? lastPage?.pagination?.page + 1
        : undefined;
    },
    enabled: enabled,
  });
};

export const useGetParentStudents = (parentId: number, enabled: boolean) => {
  return useQuery({
    queryKey: ["getParentStudents", parentId],
    queryFn: () => adminService.getParentStudents(parentId),
    enabled: enabled,
  });
};

export const useUpdateParentStudents = () => {
  return useMutation({
    mutationKey: ["updateParentStudents"],
    mutationFn: ({
      parentId,
      student_ids,
    }: {
      parentId: number;
      student_ids: number[];
    }) => adminService.updateParentStudents(parentId, student_ids),
  });
};

export const useGetImportList = (params: BaseGetRequestParams) => {
  return useQuery({
    queryKey: ["getImportList", params],
    queryFn: () => adminService.getImportList(params),
  });
};

export const useGetImportErrorDetails = (
  importId: string,
  params: BaseGetRequestParams,
) => {
  return useQuery({
    queryKey: ["getImportErrorDetails", importId, params],
    queryFn: () => adminService.getImportErrorDetails(importId, params),
    enabled: !!importId,
  });
};

export const useEnrollIndividualMentor = () => {
  return useMutation({
    mutationKey: ["enrollIndividualMentor"],
    mutationFn: ({ mentorId }: { mentorId: number }) =>
      adminService.enrollIndividualMentor(mentorId),
  });
};

export const useEnrollMultipleMentors = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["enrollMultipleMentors"],
    mutationFn: ({ mentorIds }: { mentorIds: number[] }) =>
      adminService.enrollMultipleMentors(mentorIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getUnenrolledMentors"] });
    },
  });
};

export const useGetPermissions = () => {
  return useQuery({
    queryKey: ["getPermissions"],
    queryFn: () => adminService.getUserPermissions(),
  });
};

export const useUpdateMentorPublishPermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["updateMentorPublishPermission"],
    mutationFn: ({
      schoolId,
      enabled,
    }: {
      schoolId: string | number;
      enabled: boolean;
    }) => adminService.updateMentorPublishPermission(schoolId, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["school"] });
    },
  });
};

export const useUpdateUserPermissions = () => {
  return useMutation({
    mutationKey: ["updateUserPermissions"],
    mutationFn: ({
      payload,
      tenantSlug,
    }: {
      payload: DenialsPayload;
      tenantSlug: string;
    }) => adminService.updateUserPermissions(payload, tenantSlug),
  });
};

export const useGetUserDenials = (tenantSlug: string) => {
  return useQuery({
    queryKey: ["getUserDenials", tenantSlug],
    queryFn: () => adminService.getUserDenials(tenantSlug),
  });
};

export const useAddMentorToClass = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["addMentorToClass"],
    mutationFn: ({
      mentorId,
      assignments,
      isSchool = true,
      tenantSlug = "",
    }: {
      mentorId: number;
      assignments: Array<{ className: string; schoolYear: number }>;
      isSchool?: boolean;
      tenantSlug?: string;
    }) =>
      adminService.addMentorToClass(
        mentorId,
        assignments,
        isSchool,
        tenantSlug,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getMentors"] });
    },
  });
};
